import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { products as legacyProducts } from '../../../mocks/tienda';
import { useSupabaseProducts } from '../../../hooks/useSupabaseProducts';
import { useCart } from '../../../context/CartContext';
import { useSEO } from '../../../hooks/useSEO';
import { SchemaMarkup } from '../../../components/SEOHead';
import { productSlugPath, productSlugUrl, parseProductId } from '../../../utils/slug';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';

const SITE_URL = 'https://www.anthonysanchez.es';

// Compatibilidad: los ids del catálogo local antiguo -> id equivalente en Supabase (por nombre)
const normalizeName = (s: string) =>
  s.toLowerCase().replace(/["“”]/g, '').replace(/\s+/g, ' ').trim();

const availabilityLabels = {
  'disponible': 'En stock — Disponible para envío inmediato',
  'encargo': 'Bajo encargo — Consulta disponibilidad',
  'agotado': 'Agotado — Consulta reposición',
};

const availabilityBadgeStyles = {
  'disponible': 'bg-brand-sage text-brand-black',
  'encargo': 'bg-brand-black text-white',
  'agotado': 'bg-brand-gray-200 text-brand-gray-500',
};

export default function TiendaProductDetail() {
  const { slug } = useParams<{ slug: string }>();
  const id = parseProductId(slug);
  const navigate = useNavigate();
  const { products, loading } = useSupabaseProducts();
  const product = products.find((p) => p.id === id);
  const { addItem, setIsCartOpen } = useCart();

  const [selectedImage, setSelectedImage] = useState(0);
  const [showContactModal, setShowContactModal] = useState(false);
  const [addedFeedback, setAddedFeedback] = useState(false);

  // Compatibilidad con URLs antiguas: si el id pertenece al catálogo local previo,
  // redirige al producto equivalente de Supabase (emparejado por nombre).
  useEffect(() => {
    if (loading || product) return;
    const legacy = legacyProducts.find((p) => p.id === id);
    if (!legacy) return;
    const match = products.find((p) => normalizeName(p.name) === normalizeName(legacy.name));
    if (match) navigate(productSlugUrl(match), { replace: true });
  }, [loading, product, id, products, navigate]);

  // URL canónica: si falta o no coincide el slug de nombre, redirige sin recargar
  // (mejora SEO y normaliza enlaces antiguos que solo llevaban el id numérico).
  useEffect(() => {
    if (!product) return;
    const canonical = productSlugPath(product);
    if (slug !== canonical) navigate(`/tienda/${canonical}`, { replace: true });
  }, [product, slug, navigate]);

  const handleAddToCart = () => {
    if (!product) return;
    addItem({
      productId: product.id,
      name: product.name,
      price: product.price,
      image: product.image,
      category: product.category,
    });
    setAddedFeedback(true);
    setIsCartOpen(true); // Open the cart drawer
    setTimeout(() => setAddedFeedback(false), 2000);
  };

  const effectiveStock = product?.stock ?? 0;
  const isOutOfStock = product?.availability === 'disponible' && effectiveStock <= 0;
  const isLowStock = product?.availability === 'disponible' && effectiveStock > 0 && effectiveStock <= 3;

  useSEO({
    title: product ? `${product.name} | ${product.collection} | Anthony Sánchez Couture` : 'Producto | Anthony Sánchez Couture',
    description: product ? `${product.description.substring(0, 160)}` : 'Detalle de producto del atelier Anthony Sánchez Couture en Barcelona.',
    canonical: product ? productSlugUrl(product) : '/tienda',
    ogTitle: product ? `${product.name} — ${product.price.toLocaleString('es-ES')} € | ${product.collection}` : 'Producto',
    ogDescription: product ? product.description.substring(0, 200) : 'Pieza exclusiva del atelier de alta costura en Barcelona.',
    ogImage: product?.image,
    ogType: 'product',
  });

  const productSchema = product ? {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.name,
    image: product.images?.length ? product.images : [product.image],
    description: product.description,
    brand: {
      '@type': 'Brand',
      name: 'Anthony Sánchez Couture',
    },
    offers: {
      '@type': 'Offer',
      url: `${SITE_URL}${productSlugUrl(product)}`,
      priceCurrency: 'EUR',
      price: product.price.toString(),
      availability: product.availability === 'disponible' ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
      seller: {
        '@type': 'LocalBusiness',
        name: 'Anthony Sánchez Couture',
        address: {
          '@type': 'PostalAddress',
          streetAddress: 'Rambla de Catalunya, 85, Principal - 2',
          addressLocality: 'Barcelona',
          addressRegion: 'Cataluña',
          postalCode: '08008',
          addressCountry: 'ES',
        },
      },
    },
  } : null;

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Inicio',
        item: SITE_URL,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Tienda',
        item: `${SITE_URL}/tienda`,
      },
      product ? {
        '@type': 'ListItem',
        position: 3,
        name: product.name,
        item: `${SITE_URL}${productSlugUrl(product)}`,
      } : null,
    ].filter(Boolean),
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <i className="ri-loader-4-line animate-spin text-brand-gray-400 text-3xl" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h2 className="font-serif text-brand-black text-2xl">Producto no encontrado</h2>
          <button
            onClick={() => navigate('/tienda')}
            className="mt-4 font-sans text-brand-black text-sm tracking-widest uppercase border-b border-brand-black pb-1"
          >
            Volver al catálogo
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <SchemaMarkup schema={productSchema || breadcrumbSchema} />
      <SchemaMarkup schema={breadcrumbSchema} />
      {/* Breadcrumb */}
      <div className="pt-32 pb-6 px-6 lg:px-[7%]">
        <nav className="flex items-center gap-2 font-sans text-sm text-brand-gray-400">
          <button onClick={() => navigate('/tienda')} className="hover:text-brand-black transition-colors">
            Tienda
          </button>
          <i className="ri-arrow-right-s-line text-xs" />
          <span className="text-brand-black">{product.name}</span>
        </nav>
      </div>

      {/* Product Content */}
      <div className="px-6 lg:px-[7%] pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-[55%_45%] gap-10 lg:gap-16">
          {/* Image Gallery */}
          <div>
            <div className="aspect-[3/4] rounded-[28px] overflow-hidden bg-brand-gray-50">
              <img
                src={product.images[selectedImage]}
                alt={product.name}
                className="w-full h-full object-cover object-top"
              />
            </div>
            {product.images.length > 1 && (
              <div className="flex gap-3 mt-4">
                {product.images.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setSelectedImage(i)}
                    className={`w-20 h-20 rounded-xl overflow-hidden border-2 transition-all ${
                      i === selectedImage ? 'border-brand-black' : 'border-transparent'
                    }`}
                  >
                    <img src={img} alt={`${product.name} ${i + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Info */}
          <div className="lg:sticky lg:top-32 lg:self-start">
            <span className="font-sans text-brand-gray-400 text-[11px] tracking-[0.2em] uppercase font-medium">
              {product.collection}
            </span>
            <h1 className="font-serif text-brand-black text-[clamp(1.8rem,3vw,2.5rem)] font-medium leading-[1.15] mt-3">
              {product.name}
            </h1>

            <p className="font-sans text-brand-black text-2xl font-semibold mt-5">
              {product.price.toLocaleString('es-ES', {
                style: 'currency',
                currency: 'EUR',
                minimumFractionDigits: 0,
              })}
            </p>

            <span className={`inline-block mt-4 px-4 py-1.5 rounded-full font-sans text-[11px] tracking-wider uppercase font-medium ${availabilityBadgeStyles[product.availability]}`}>
              {availabilityLabels[product.availability]}
            </span>

            {product.availability === 'disponible' && (
              <div className="mt-3 flex items-center gap-2">
                {isOutOfStock ? (
                  <span className="font-sans text-red-500 text-xs font-medium">
                    <i className="ri-close-circle-line mr-1" />Agotado temporalmente
                  </span>
                ) : (
                  <span className="font-sans text-brand-gray-500 text-xs">
                    <i className="ri-archive-line mr-1" />
                    {effectiveStock} {effectiveStock === 1 ? 'unidad disponible' : 'unidades en stock'}
                    {isLowStock && (
                      <span className="text-amber-600 font-medium ml-1">— ¡Quedan pocas!</span>
                    )}
                  </span>
                )}
              </div>
            )}

            <p className="font-sans text-brand-gray-500 text-[15px] leading-[1.8] mt-6">
              {product.description}
            </p>

            {/* CTA */}
            <div className="mt-8 space-y-3">
              {product.availability === 'disponible' && !isOutOfStock ? (
                <>
                  <button
                    onClick={handleAddToCart}
                    className="w-full bg-brand-black text-white font-sans text-sm tracking-widest uppercase py-4 rounded-full hover:bg-brand-gray-600 transition-all duration-300 whitespace-nowrap relative overflow-hidden"
                  >
                    {addedFeedback ? (
                      <span className="flex items-center justify-center gap-2">
                        <i className="ri-check-line" /> Añadido a la cesta
                      </span>
                    ) : (
                      'Añadir a la cesta'
                    )}
                  </button>
                  <button
                    onClick={() => { handleAddToCart(); setIsCartOpen(true); }}
                    className="w-full border border-brand-gray-200 text-brand-black font-sans text-sm tracking-widest uppercase py-4 rounded-full hover:border-brand-black transition-all duration-300 whitespace-nowrap"
                  >
                    Comprar ahora
                  </button>
                </>
              ) : product.availability === 'disponible' && isOutOfStock ? (
                <>
                  <button
                    disabled
                    className="w-full bg-brand-gray-200 text-brand-gray-500 font-sans text-sm tracking-widest uppercase py-4 rounded-full cursor-not-allowed whitespace-nowrap"
                  >
                    Agotado
                  </button>
                  <button
                    onClick={() => setShowContactModal(true)}
                    className="w-full border border-brand-gray-200 text-brand-black font-sans text-sm tracking-widest uppercase py-4 rounded-full hover:border-brand-black transition-all duration-300 whitespace-nowrap"
                  >
                    Consultar Reposición
                  </button>
                </>
              ) : (
                <button
                  onClick={() => setShowContactModal(true)}
                  className="w-full bg-brand-black text-white font-sans text-sm tracking-widest uppercase py-4 rounded-full hover:bg-brand-gray-600 transition-all duration-300 whitespace-nowrap"
                >
                  Solicitar Información
                </button>
              )}
              <button
                onClick={() => {
                  if (product.category === 'alta-costura') {
                    navigate('/alta-costura');
                  } else {
                    setShowContactModal(true);
                  }
                }}
                className="w-full border border-brand-gray-200 text-brand-black font-sans text-sm tracking-widest uppercase py-4 rounded-full hover:border-brand-black transition-all duration-300 whitespace-nowrap"
              >
                {product.category === 'alta-costura' ? 'Reservar Cita Privada' : 'Consultar Tallas'}
              </button>
            </div>

            <p className="font-sans text-brand-gray-400 text-xs mt-3 text-center">
              Entrega estimada: {product.deliveryTime}
            </p>

            {/* Details Accordion */}
            <div className="mt-10 space-y-3">
              <details className="group bg-brand-gray-50 rounded-2xl">
                <summary className="flex items-center justify-between p-5 cursor-pointer font-sans text-brand-black text-sm font-medium">
                  <span>Detalles de la prenda</span>
                  <i className="ri-add-line text-brand-gray-400 group-open:rotate-45 transition-transform" />
                </summary>
                <div className="px-5 pb-5">
                  <ul className="space-y-2">
                    {product.details.map((detail, i) => (
                      <li key={i} className="flex items-start gap-2 font-sans text-brand-gray-500 text-[14px] leading-relaxed">
                        <i className="ri-check-line text-brand-sage mt-0.5" />
                        {detail}
                      </li>
                    ))}
                  </ul>
                </div>
              </details>

              <details className="group bg-brand-gray-50 rounded-2xl">
                <summary className="flex items-center justify-between p-5 cursor-pointer font-sans text-brand-black text-sm font-medium">
                  <span>Materiales</span>
                  <i className="ri-add-line text-brand-gray-400 group-open:rotate-45 transition-transform" />
                </summary>
                <div className="px-5 pb-5">
                  <p className="font-sans text-brand-gray-500 text-[14px] leading-relaxed">
                    {product.materials}
                  </p>
                </div>
              </details>

              <details className="group bg-brand-gray-50 rounded-2xl">
                <summary className="flex items-center justify-between p-5 cursor-pointer font-sans text-brand-black text-sm font-medium">
                  <span>Tallas y medidas</span>
                  <i className="ri-add-line text-brand-gray-400 group-open:rotate-45 transition-transform" />
                </summary>
                <div className="px-5 pb-5">
                  <p className="font-sans text-brand-gray-500 text-[14px] leading-relaxed">
                    {product.measurements}
                  </p>
                </div>
              </details>
            </div>
          </div>
        </div>
      </div>

      {/* Contact Modal */}
      {showContactModal && (
        <div
          className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4"
          onClick={() => setShowContactModal(false)}
        >
          <div
            className="bg-white rounded-[28px] p-8 md:p-12 max-w-lg w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-serif text-brand-black text-2xl font-medium">
                  {product.availability === 'disponible' ? 'Consultar Compra' : 'Solicitar Información'}
                </h3>
                <p className="font-sans text-brand-gray-500 text-sm mt-2">
                  {product.name} — {product.price.toLocaleString('es-ES', {
                    style: 'currency',
                    currency: 'EUR',
                    minimumFractionDigits: 0,
                  })}
                </p>
              </div>
              <button
                onClick={() => setShowContactModal(false)}
                className="w-8 h-8 flex items-center justify-center text-brand-gray-400 hover:text-brand-black transition-colors"
              >
                <i className="ri-close-line text-xl" />
              </button>
            </div>

            <div className="mt-8 space-y-5">
              <div className="flex items-center gap-4 p-4 bg-brand-gray-50 rounded-xl">
                <div className="w-10 h-10 rounded-full bg-brand-sage flex items-center justify-center flex-shrink-0">
                  <i className="ri-phone-line text-brand-black" />
                </div>
                <div>
                  <span className="font-sans text-brand-black text-sm font-medium">Llamar al atelier</span>
                  <p className="font-sans text-brand-gray-400 text-xs">Disponible de lunes a viernes, 10:00-19:00</p>
                </div>
              </div>

              <div className="flex items-center gap-4 p-4 bg-brand-gray-50 rounded-xl">
                <div className="w-10 h-10 rounded-full bg-brand-sage flex items-center justify-center flex-shrink-0">
                  <i className="ri-mail-line text-brand-black" />
                </div>
                <div>
                  <span className="font-sans text-brand-black text-sm font-medium">Enviar email</span>
                  <p className="font-sans text-brand-gray-400 text-xs">Responderemos en menos de 24 horas</p>
                </div>
              </div>

              <div className="flex items-center gap-4 p-4 bg-brand-gray-50 rounded-xl">
                <div className="w-10 h-10 rounded-full bg-brand-sage flex items-center justify-center flex-shrink-0">
                  <i className="ri-calendar-check-line text-brand-black" />
                </div>
                <div>
                  <span className="font-sans text-brand-black text-sm font-medium">Reservar cita presencial</span>
                  <p className="font-sans text-brand-gray-400 text-xs">Rambla de Catalunya, 85, Principal - 2, Eixample, Barcelona</p>
                </div>
              </div>
            </div>

            <div className="mt-8 grid grid-cols-2 gap-3">
              <button
                onClick={() => {
                  setShowContactModal(false);
                  navigate('/contacto');
                }}
                className="bg-brand-black text-white font-sans text-sm py-3.5 rounded-full hover:bg-brand-gray-600 transition-all whitespace-nowrap"
              >
                Ir a Contacto
              </button>
              <button
                onClick={() => {
                  setShowContactModal(false);
                  if (product.category === 'alta-costura') {
                    navigate('/alta-costura');
                  } else {
                    navigate('/pret-a-porter');
                  }
                }}
                className="border border-brand-gray-200 text-brand-black font-sans text-sm py-3.5 rounded-full hover:border-brand-black transition-all whitespace-nowrap"
              >
                {product.category === 'alta-costura' ? 'Reservar Cita' : 'Consultar'}
              </button>
            </div>
          </div>
        </div>
      )}
      <Footer />
    </div>
  );
}